import { Component } from '@theme/component';

/**
 * Studio mosaic (sections/studio-mosaic.liquid): tiles fade up as they scroll into view, a few
 * at a time, and each photo settles in once it has loaded rather than popping over the
 * placeholder.
 *
 * Progressive enhancement: the hidden starting state only applies once this script sets
 * `data-reveal` on the section, so without JS, or under reduced motion, every tile is simply
 * there.
 *
 * @typedef {{ tiles?: HTMLElement[] }} StudioMosaicRefs
 * @extends {Component<StudioMosaicRefs>}
 */
class StudioMosaicComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const tiles = this.refs.tiles ?? [];
    if (!tiles.length) return;

    for (const tile of tiles) this.#watchImage(tile);

    this.dataset.reveal = '';
    this.#observer = new IntersectionObserver(this.#onIntersect, { rootMargin: '0px 0px -8% 0px' });
    for (const tile of tiles) this.#observer.observe(tile);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    // Tiles that arrive together (a row, or the whole first screen) stagger left to right.
    const arriving = entries.filter((entry) => entry.isIntersecting).map((entry) => /** @type {HTMLElement} */ (entry.target));
    arriving.forEach((tile, index) => {
      tile.style.setProperty('--reveal-delay', `${Math.min(index, 5) * 80}ms`);
      tile.dataset.shown = '';
      this.#observer?.unobserve(tile);
    });
  };

  /** @param {HTMLElement} tile */
  #watchImage(tile) {
    const image = tile.querySelector('img');
    if (!image) {
      tile.dataset.loaded = '';
      return;
    }
    const done = () => (tile.dataset.loaded = '');
    if (image.complete && image.naturalWidth > 0) done();
    else {
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
    }
  }
}

if (!customElements.get('studio-mosaic-component')) {
  customElements.define('studio-mosaic-component', StudioMosaicComponent);
}
